import {
	Button,
	ButtonGroup,
	DialogTrigger,
	Heading,
	Popover,
	Text,
} from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { type ReactNode, useEffect, useState } from 'react';

interface DeleteConfirmationWrapperProps {
	children: ReactNode;
	onConfirm: () => Promise<void>;
	onCancel: () => void;
	isOpen: boolean;
	title?: string;
	body?: string;
}

const DEFAULT_TITLE = 'Delete post?';
const DEFAULT_BODY =
	'This post will be removed from your Content Library and will not be posted to Instagram.';

export const DeleteConfirmationWrapper = ({
	children,
	onConfirm,
	onCancel,
	isOpen,
	title = DEFAULT_TITLE,
	body = DEFAULT_BODY,
}: DeleteConfirmationWrapperProps) => {
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		if (!isOpen) {
			setIsDeleting(false);
		}
	}, [isOpen]);

	const handleConfirm = async () => {
		setIsDeleting(true);
		try {
			await onConfirm();
		} finally {
			setIsDeleting(false);
		}
	};

	const handleCancel = () => {
		setIsDeleting(false);
		onCancel();
	};

	return (
		<DialogTrigger>
			{children}
			<Popover
				placement="bottom"
				hideArrow={true}
				size="M"
				isOpen={isOpen}
				onOpenChange={(nextOpen) => {
					if (!nextOpen) {
						handleCancel();
					}
				}}
			>
				<div
					className="flex flex-col gap-6 p-6"
					style={{
						backgroundColor: 'layer-2',
						minWidth: '320px',
						maxWidth: '480px',
					}}
				>
					<Heading
						styles={style({
							font: 'title',
							color: 'heading',
							fontSize: 'title-2xl',
							fontWeight: 'bold',
						})}
					>
						{title}
					</Heading>

					<Text
						styles={style({
							font: 'body',
							color: 'body',
							fontSize: 'body',
						})}
					>
						{body}
					</Text>

					<ButtonGroup UNSAFE_className="justify-end gap-3">
						<Button variant="secondary" onPress={handleCancel}>
							<Text>Cancel</Text>
						</Button>
						<Button
							variant="negative"
							onPress={handleConfirm}
							isPending={isDeleting}
						>
							<Text>Yes, delete</Text>
						</Button>
					</ButtonGroup>
				</div>
			</Popover>
		</DialogTrigger>
	);
};
